import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Building2, MapPin, Clock, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const CommercialEPCSouthampton = () => {
  const faqs = [
    {
      question: "How much does a Commercial EPC cost in Southampton?",
      answer: "Commercial EPC pricing in Southampton varies by property type and size. Small offices/retail (up to 500 sq m) typically cost £180-£400. Medium warehouses (500-2000 sq m) range from £400-£800. Larger properties £800-£2000+. Properties in Southampton, Portsmouth, Winchester and surrounding Hampshire areas are within our regular service area."
    },
    {
      question: "Do you cover all Southampton and Hampshire for Commercial EPCs?",
      answer: "Yes, we cover Southampton, Portsmouth, Winchester, Eastleigh, Fareham, Gosport, Havant, Basingstoke, Andover, Romsey, Lymington, New Milton, Ringwood and all surrounding Hampshire areas. We also serve the wider South Coast region."
    },
    {
      question: "What types of Southampton commercial property do you assess?",
      answer: "We assess all non-domestic buildings in Southampton including offices, retail units, warehouses, industrial premises, hotels, port facilities, marine businesses, restaurants, pubs, cafes, care homes, schools, university buildings, and mixed-use properties across Southampton and Hampshire."
    },
    {
      question: "How long does a Commercial EPC take in Southampton?",
      answer: "The on-site survey typically takes 1-3 hours depending on property size. For Southampton and Hampshire properties, we aim to schedule surveys within 5-7 working days of booking, with certificate delivery within 5-7 working days of the survey. Expedited service available for urgent transactions."
    },
    {
      question: "Do Southampton commercial landlords need EPC rating E or above?",
      answer: "Yes, since April 2023, all commercial properties in Southampton must have an EPC rating of E or above to be legally rented, unless you have a valid exemption. Properties rated F or G require improvement. We provide Energy Advice Reports to help Southampton landlords achieve compliance."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Commercial EPC Southampton | Non-Domestic Energy Certificates | Hampshire, Portsmouth, Winchester</title>
        <meta name="description" content="Professional Commercial EPC assessments in Southampton from £180. NDEA qualified assessors covering Portsmouth, Winchester, Eastleigh, Fareham. Offices, warehouses, retail, marine. Call 01392 980077." />
        <meta name="keywords" content="commercial EPC Southampton, non-domestic EPC Hampshire, commercial EPC Portsmouth, office EPC Southampton, warehouse EPC Hampshire, NDEA Southampton, business EPC Southampton" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/commercial-epc-southampton" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Covering Southampton & Hampshire</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Commercial EPC Southampton
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional Non-Domestic Energy Performance Certificates for businesses across Southampton, Portsmouth, Winchester and Hampshire. NDEA Level 3 & 4 qualified assessors.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">From £180</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">NDEA Qualified</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">Fast Turnaround</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700">
                    Book Commercial EPC
                  </Button>
                </Link>
                <a href="tel:01onal980077">
                  <Button size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                    <Phone className="mr-2 h-5 w-5" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Commercial EPC Services in Southampton</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Office EPCs Southampton</h3>
                    <p className="text-gray-600">
                      Commercial EPCs for offices throughout Southampton city centre, Ocean Village, Southampton Business Park, Eastleigh and surrounding business districts.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Warehouse EPCs Hampshire</h3>
                    <p className="text-gray-600">
                      Industrial and warehouse EPCs for logistics centres, distribution hubs, port facilities, and industrial estates across Southampton and Portsmouth.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Retail EPCs Southampton</h3>
                    <p className="text-gray-600">
                      Shop and retail unit EPCs for Southampton's West Quay, high street locations, retail parks, and shopping centres across Hampshire.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Hotel & Hospitality EPCs</h3>
                    <p className="text-gray-600">
                      Commercial EPCs for hotels, B&Bs, restaurants, pubs, and hospitality venues across Southampton, Portsmouth, Winchester and the New Forest.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Marine & Port Facilities</h3>
                    <p className="text-gray-600">
                      Specialist EPCs for marine businesses, cruise terminal facilities, port operations, boatyards and maritime commercial premises.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">University & Education EPCs</h3>
                    <p className="text-gray-600">
                      Specialist EPCs for educational buildings, university facilities, colleges, and schools across Southampton and Hampshire.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Areas We Cover in Southampton & Hampshire</h2>
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Southampton</h3>
                  <p className="text-sm text-gray-600">City Centre, Ocean Village, Portswood, Shirley, Bitterne, Woolston, Millbrook</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Portsmouth</h3>
                  <p className="text-sm text-gray-600">Gunwharf, Cosham, Hilsea, Fratton, Southsea, Port Solent, Lakeside</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Winchester</h3>
                  <p className="text-sm text-gray-600">City Centre, Bar End, Kings Worthy, Winnall, Badger Farm</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Eastleigh</h3>
                  <p className="text-sm text-gray-600">Town Centre, Chandlers Ford, Hedge End, Botley, Fair Oak</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Fareham</h3>
                  <p className="text-sm text-gray-600">Town Centre, Whiteley, Locks Heath, Titchfield, Park Gate</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Basingstoke</h3>
                  <p className="text-sm text-gray-600">Town Centre, Chineham, Brighton Hill, Old Basing, Oakley</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Why Choose Us for Southampton Commercial EPCs?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-4">
                  <Award className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">NDEA Qualified Assessors</h3>
                    <p className="text-gray-600">Our assessors hold Level 3 and Level 4 NDEA qualifications, enabling assessment of all commercial property types from small shops to complex industrial facilities.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Clock className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">Fast Turnaround</h3>
                    <p className="text-gray-600">We understand Southampton's fast-moving commercial property market. Surveys typically within 5-7 days with certificates delivered promptly.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <MapPin className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">Full Hampshire Coverage</h3>
                    <p className="text-gray-600">Regular coverage across Southampton, Portsmouth, Winchester, Eastleigh, Fareham, Gosport, Havant, Basingstoke and all Hampshire areas.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Building2 className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">All Property Types</h3>
                    <p className="text-gray-600">From small retail units to large distribution centres, marine facilities to university buildings, we assess all commercial property types in Southampton.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Commercial EPC Southampton FAQs</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-green-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Book Your Southampton Commercial EPC Today</h2>
            <p className="text-xl mb-8 opacity-90">
              Professional NDEA assessments for all commercial property types across Southampton and Hampshire
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking">
                <Button size="lg" variant="secondary" className="bg-white text-green-600 hover:bg-gray-100">
                  Book Online Now
                </Button>
              </Link>
              <a href="tel:01392980077">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-green-700">
                  <Phone className="mr-2 h-5 w-5" />
                  01392 980077
                </Button>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CommercialEPCSouthampton;
